import React, { useEffect, useState } from "react";
import { X, Save, Trash2, Copy, Download } from "lucide-react";
import { toast } from "sonner";

import { projectsApi, exportApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";

// Common IANA timezones for festival production. Not exhaustive — users can type
// anything; we just give them a quick-pick of the most likely options.
const TZ_PRESETS = [
  "Europe/London",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Australia/Sydney",
  "Asia/Tokyo",
  "UTC",
];

/**
 * Project settings dialog. Edit name + dates + venue + timezone. Owner can also
 * Duplicate (with optional new-start-date for date-shifted blocks) and Delete.
 *
 * Read-only for `member` role: the form inputs stay disabled and the destructive
 * actions are hidden.
 */
export default function ProjectSettingsDialog({
  open,
  onClose,
  project,
  myRole,
  onMutated,
  onProjectDeleted,
  onProjectDuplicated,
}) {
  const [name, setName] = useState(project?.name || "");
  const [venue, setVenue] = useState(project?.venue_name || "");
  const [tz, setTz] = useState(project?.timezone || "Europe/London");
  const [startDate, setStartDate] = useState(project?.start_date || "");
  const [endDate, setEndDate] = useState(project?.end_date || "");
  const [busy, setBusy] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [dupName, setDupName] = useState("");
  const [dupNewStart, setDupNewStart] = useState("");

  useEffect(() => {
    if (open && project) {
      setName(project.name || "");
      setVenue(project.venue_name || "");
      setTz(project.timezone || "Europe/London");
      setStartDate(project.start_date || "");
      setEndDate(project.end_date || "");
      setDuplicateOpen(false);
      setDupName(`${project.name || ""} (copy)`);
      setDupNewStart("");
    }
  }, [open, project]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open || !project) return null;
  const isOwner = myRole === "owner";

  const save = async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Name required");
      return;
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      toast.error("End date must be ≥ start date");
      return;
    }
    setBusy(true);
    try {
      await projectsApi.update(project.id, {
        name: cleanName,
        venue_name: venue.trim() || null,
        timezone: tz,
        start_date: startDate || null,
        end_date: endDate || null,
      });
      toast.success("Project saved");
      onMutated && onMutated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    const ok = await confirm({
      title: `Delete "${project.name}"?`,
      body: "Soft-delete — data is hidden but recoverable from the database. All members will lose access.",
      confirmLabel: "DELETE PROJECT",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await projectsApi.remove(project.id);
      toast.success("Project deleted");
      onProjectDeleted && onProjectDeleted(project.id);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  const doDuplicate = async () => {
    setBusy(true);
    try {
      const payload = { new_name: dupName.trim() || `${project.name} (copy)` };
      if (dupNewStart) {
        payload.new_start_date = dupNewStart;
        // Auto-derive end_date by preserving span
        if (project.start_date && project.end_date) {
          const span = (new Date(project.end_date) - new Date(project.start_date)) / 86400000;
          const newEnd = new Date(dupNewStart);
          newEnd.setDate(newEnd.getDate() + span);
          payload.new_end_date = newEnd.toISOString().slice(0, 10);
        }
      }
      const r = await projectsApi.duplicate(project.id, payload);
      const c = r.counts || {};
      toast.success(
        `Duplicated · ${c.tasks} task${c.tasks === 1 ? "" : "s"}, ${c.blocks} block${c.blocks === 1 ? "" : "s"}`
      );
      onProjectDuplicated && onProjectDuplicated(r.project);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    } finally {
      setBusy(false);
    }
  };

  const doExport = async () => {
    setBusy(true);
    try {
      const blob = await exportApi.fetch(project.id);
      const json = JSON.stringify(blob, null, 2);
      const file = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const slug = (project.name || "project").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${slug}-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={busy ? undefined : onClose}
      data-testid="project-settings-overlay"
    >
      <div
        className="w-[560px] max-w-full bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="project-settings-dialog"
      >
        <header className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">PROJECT</div>
            <div className="text-sm text-ink mt-0.5">{project.name}</div>
          </div>
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="project-settings-close">
            <X className="w-3 h-3" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4 space-y-3">
          <Field label="NAME">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isOwner && myRole !== "member"}
              className="w-full bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              data-testid="settings-name"
            />
          </Field>

          <Field label="VENUE">
            <input
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="e.g. Riverside Park, Bristol"
              className="w-full bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              data-testid="settings-venue"
            />
          </Field>

          <Field label="TIMEZONE">
            <input
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              list="tz-presets"
              className="w-full mono bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              data-testid="settings-timezone"
            />
            <datalist id="tz-presets">
              {TZ_PRESETS.map((t) => <option key={t} value={t} />)}
            </datalist>
            <div className="mono text-2xs text-ink-mute uppercase tracking-widest mt-1">
              IANA name (e.g. Europe/London). Affects all displayed local times.
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="START DATE">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full mono tabular-nums bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
                data-testid="settings-start-date"
              />
            </Field>
            <Field label="END DATE">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full mono tabular-nums bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
                data-testid="settings-end-date"
              />
            </Field>
          </div>

          {/* Backup / Export JSON */}
          <div className="border border-line mt-4">
            <button
              type="button"
              onClick={doExport}
              disabled={busy}
              className="w-full flex items-center gap-2 px-3 py-2 bg-bg-elevated hover:bg-bg-highlight transition-colors text-left disabled:opacity-50"
              data-testid="settings-backup-btn"
              title="Download a complete JSON dump of every resource on this project"
            >
              <Download className="w-3 h-3 text-cyan" />
              <span className="mono text-2xs uppercase tracking-widest text-ink">BACKUP · EXPORT AS JSON</span>
              <span className="text-2xs text-ink-mute ml-auto">↓ .json</span>
            </button>
          </div>

          {/* Duplicate sub-panel */}
          <div className="border border-line mt-3">
            <button
              type="button"
              onClick={() => setDuplicateOpen((o) => !o)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-bg-elevated hover:bg-bg-highlight transition-colors text-left"
              data-testid="settings-duplicate-toggle"
            >
              <Copy className="w-3 h-3 text-cyan" />
              <span className="mono text-2xs uppercase tracking-widest text-ink">DUPLICATE PROJECT</span>
              <span className="text-2xs text-ink-mute ml-auto">{duplicateOpen ? "−" : "+"}</span>
            </button>
            {duplicateOpen && (
              <div className="p-3 space-y-2 border-t border-line">
                <Field label="NEW NAME">
                  <input
                    value={dupName}
                    onChange={(e) => setDupName(e.target.value)}
                    className="w-full bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
                    data-testid="settings-dup-name"
                  />
                </Field>
                <Field label="NEW START DATE (OPTIONAL)">
                  <input
                    type="date"
                    value={dupNewStart}
                    onChange={(e) => setDupNewStart(e.target.value)}
                    className="w-full mono tabular-nums bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
                    data-testid="settings-dup-start"
                  />
                  <div className="mono text-2xs text-ink-mute uppercase tracking-widest mt-1">
                    With a date: blocks shift to land on new dates · without: clean task template
                  </div>
                </Field>
                <button
                  type="button"
                  onClick={doDuplicate}
                  disabled={busy}
                  className="w-full btn-primary justify-center"
                  data-testid="settings-dup-confirm"
                >
                  <Copy className="w-3 h-3" /> DUPLICATE
                </button>
              </div>
            )}
          </div>
        </div>

        <footer className="flex items-center gap-2 p-3 border-t border-line-strong bg-bg-elevated">
          {isOwner && (
            <button
              type="button"
              onClick={doDelete}
              disabled={busy}
              className="text-red hover:text-red-light hover:bg-red-glow border border-red/40 px-3 py-1.5 mono text-2xs uppercase tracking-widest transition-colors"
              data-testid="settings-delete"
              title="Delete this project (owner only)"
            >
              <Trash2 className="w-3 h-3 inline mr-1" /> DELETE
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="settings-cancel">
            CANCEL
          </button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="settings-save">
            <Save className="w-3 h-3" /> SAVE
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute mb-1">{label}</div>
      {children}
    </div>
  );
}
